import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Output, EventEmitter, HostListener, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { InputTextModule } from 'primeng/inputtext';
import { Product } from '../../../models/product.model';
import { ProductService } from '../../../services/product.service';
import { Subject, EMPTY, debounceTime, distinctUntilChanged, switchMap, tap, catchError, finalize, takeUntil } from 'rxjs';

@Component({
  selector: 'app-product-search',
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule
  ],
  templateUrl: './product-search.component.html'
})
export class ProductSearchComponent implements OnInit, OnDestroy {
  @Output() productSelected = new EventEmitter<{product: Product, amount: number}>();
  @Output() codeSearchRequested = new EventEmitter<{searchTerm: string, amount: number}>();
  @ViewChild('resultsContainer') resultsContainer?: ElementRef<HTMLDivElement>;

  search: string = '';
  searchResults: Product[] = []; // Público para permitir acesso do componente pai
  selectedProductIndex: number = -1;
  isSearching: boolean = false;
  private searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private productService: ProductService
  ) {}

  ngOnInit(): void {
    this.setupProductSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupProductSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        if (!(term && term.length >= 3)) {
          this.searchResults = [];
          this.selectedProductIndex = -1;
          this.isSearching = false;
          return EMPTY;
        }

        this.isSearching = true;
        return this.productService.getAll(term, true, false, false, 1, true, false).pipe(
          tap((response) => {
            this.searchResults = response.data || [];
            this.selectedProductIndex = this.searchResults.length > 0 ? 0 : -1;
          }),
          catchError(() => {
            this.searchResults = [];
            this.selectedProductIndex = -1;
            return EMPTY;
          }),
          finalize(() => {
            this.isSearching = false;
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /**
   * Captura eventos de teclado globalmente para navegação na lista de resultados
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Verificar se está em input ou textarea
    const target = event.target as HTMLElement;
    const isInputFocused = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
    
    // Se houver resultados de busca, permitir navegação com setas
    if(this.searchResults.length > 0 && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      // Permitir navegação quando o input está focado (digitando)
      // Isso evita conflito com o PDV que gerencia navegação nos itens da venda
      if(isInputFocused) {
        event.preventDefault();
        if(event.key === "ArrowDown") {
          this.navigateDown();
        } else if(event.key === "ArrowUp") {
          this.navigateUp();
        }
      }
    }
  }

  /**
   * Método público para ser chamado pelo componente pai quando o input muda
   */
  onSearchInputChange(searchValue: string): void {
    this.search = searchValue;
    this.onSearchChange();
  }

  /**
   * Método público para ser chamado pelo componente pai quando uma tecla é pressionada
   */
  onSearchKeyup(event: any): void {
    // Se há resultados de busca e o usuário pressionar Enter, seleciona o produto
    if(event.key === "Enter"){
      if(this.searchResults.length > 0 && this.selectedProductIndex >= 0){
        // Selecionar produto da lista de busca
        const selectedProduct = this.searchResults[this.selectedProductIndex];
        const amount = Number.parseFloat(this.getAmount(this.search));
        this.productSelected.emit({product: selectedProduct, amount});
        this.clearSearch();
        return;
      }

      // Comportamento: buscar por código ou código de barras
      const amount = Number.parseFloat(this.getAmount(this.search));
      const searchTerm = this.getSearchTerm(this.search);
      
      // Emitir evento para o componente pai buscar por código
      this.codeSearchRequested.emit({searchTerm, amount});
      this.clearSearch();
    }
  }

  private onSearchChange(): void {
    const searchTerm = this.getSearchTerm();
    
    // Se for apenas números (código de barras, código interno, código de balança),
    // não mostrar a lista automaticamente - apenas busca ao pressionar Enter
    if(this.isNumericCode(searchTerm)){
      this.searchResults = [];
      this.selectedProductIndex = -1;
      // Emitir string vazia para resetar o estado do distinctUntilChanged,
      // garantindo que uma busca alfabética subsequente com o mesmo termo dispare.
      this.searchSubject.next('');
      return;
    }
    
    // Sempre emitir no subject (inclusive termos curtos/vazios) para que o
    // distinctUntilChanged enxergue todas as transições e não bloqueie o
    // próximo termo de 3+ caracteres quando o usuário repete o mesmo valor
    // após limpar o input. A guarda de tamanho mínimo é aplicada dentro do
    // pipeline em setupProductSearch().
    this.searchSubject.next(searchTerm);

    // Limpar resultados imediatamente para feedback de UI quando o termo é curto.
    if(!searchTerm || searchTerm.length < 3){
      this.searchResults = [];
      this.selectedProductIndex = -1;
    }
  }

  private isNumericCode(searchTerm: string): boolean {
    if(!searchTerm || searchTerm.length === 0) return false;
    return /^\d+$/.test(searchTerm.trim());
  }

  private getSearchTerm(searchValue?: string): string {
    const search = searchValue || this.search;
    if(search.includes('*')){
      const parts = search.split('*');
      return parts.length > 1 ? parts[1].trim() : search;
    }
    return search;
  }

  private getAmount(searchValue?: string): string {
    const search = searchValue || this.search;
    let amount = '1';
    if(search.includes('*')){
      amount = search.split('*')[0];
    }
    return amount;
  }

  selectProductFromList(product: Product): void {
    const amount = Number.parseFloat(this.getAmount(this.search));
    this.productSelected.emit({product, amount});
    this.clearSearch();
  }

  private navigateDown(): void {
    if(this.searchResults.length > 0){
      this.selectedProductIndex = Math.min(this.selectedProductIndex + 1, this.searchResults.length - 1);
      this.scrollToSelectedItem();
    }
  }

  private navigateUp(): void {
    if(this.searchResults.length > 0){
      this.selectedProductIndex = Math.max(this.selectedProductIndex - 1, -1);
      this.scrollToSelectedItem();
    }
  }

  private scrollToSelectedItem(): void {
    if (this.selectedProductIndex < 0) return;

    const container = this.resultsContainer?.nativeElement;
    if (!container) return;

    const selectedElement = container.querySelector<HTMLElement>(`#search-result-item-${this.selectedProductIndex}`);
    if (!selectedElement) return;

    selectedElement.scrollIntoView({ block: 'nearest' });
  }

  private clearSearch(): void {
    this.search = '';
    this.searchResults = [];
    this.selectedProductIndex = -1;
    // Resetar o estado do distinctUntilChanged para que repetir o mesmo termo
    // após selecionar um produto (Enter) dispare uma nova busca.
    this.searchSubject.next('');
  }
}

